import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import axiosClient from "../../../axios-client";

// Dialog style
const dialogStyle = {
  "& .MuiDialog-paper": {
    width: 600,
    maxHeight: "80vh",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 2,
  },
};

// Table head style
const tableHeadStyle = {
  "& .MuiTableCell-head": {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    color: "#333",
  },
};

// Pagination style
const paginationStyle = {
  "& .MuiTablePagination-root": {
    marginTop: "16px",
    borderTop: "1px solid #e0e0e0",
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: "0.875rem",
      color: "#555",
    },
    "& .MuiTablePagination-actions": {
      "& .MuiIconButton-root": {
        padding: "8px",
        "&:hover": {
          backgroundColor: "#e0e0e0",
        },
      },
    },
  },
};

const SkippedUsersDialog = ({ open, close, ad }) => {
  const userInfo = useSelector((state) => state.user.userInfo);
  const isLogin = useSelector((state) => state.user.isLogin);
  const user = isLogin ? JSON.parse(userInfo) : null;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [skippedUsers, setSkippedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ad?.Id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axiosClient.put(`need/listSkipUser/${ad.Id}`);
        if (response.data.statusCode === 200) {
          const filteredUsers = response.data.data.filter(
            (user) => user.skip_status === 1
          );
          setSkippedUsers(filteredUsers || []);
          setError(null);
        } else {
          setError(response.data.message || "Failed to fetch skipped users");
        }
      } catch (err) {
        setError("An error occurred while fetching skipped users");
        console.error("Error fetching skipped users:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ad]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const handleClose = () => {
    close();
    setPage(0); // Reset page on close
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="skipped-users-dialog-title"
      sx={dialogStyle}
    >
      <DialogTitle id="skipped-users-dialog-title">
        {ad?.Title || "Skipped Users"}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography>Loading...</Typography>
          </Box>
        )}
        {error && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {!loading && skippedUsers.length === 0 && !error && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography>No skipped users found.</Typography>
          </Box>
        )}
        {!loading && skippedUsers.length > 0 && (
          <>
            <Table aria-label="skipped users table">
              <TableHead sx={tableHeadStyle}>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Comment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skippedUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.user_name || "N/A"}</TableCell>
                      <TableCell>{user.comment || "N/A"}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={skippedUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={paginationStyle}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SkippedUsersDialog;
